<template>
    <div class="last-message" :class="{
        'special-message': isSpecialMessage,
        'visitor-message': lastMessageSender.includes('visitor'),
        'agent-message': lastMessageSender.includes('user'),
    }" v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [300, 0] }" :content="lastMessageText">
        <div class="message-icon" v-if="lastMessageIcon">
            <i class="material-icons">{{ lastMessageIcon }}</i>
        </div>
        <span class="message-text">{{ lastMessageText }}</span>
    </div>
</template>

<script>
import { SanitizeHtml } from "../../../utils/utils";
import { stripAllFormats } from "../../../utils/formatter";

export default {
    name: "ContactLastMessage",
    props: {
        lastMessage: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        messageTypeMapping: {
            // Media türleri
            'chatbot-image': { text: 'Media İçeriği', icon: 'image', isSpecial: true },
            'chatbot-file': { text: 'Belge', icon: 'description', isSpecial: true },
            'chatbot-gallery': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },
            'user-media': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },
            'visitor-media': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },
            'whatsapp-user-media': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },
            'instagram-user-media': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },
            'messenger-user-media': { text: 'Media İçeriği', icon: 'perm_media', isSpecial: true },

            // Mail türleri
            'user-email': { text: 'Mail İçeriği', icon: 'mail', isSpecial: true },
            'visitor-email': { text: 'Mail İçeriği', icon: 'mail', isSpecial: true },
            'system-email': { text: 'Mail İçeriği', icon: 'mail', isSpecial: true },

            // SMS
            'user-sms': { text: 'SMS Gönderimi', icon: 'sms', isSpecial: true },

            // Diğer özel türler
            'whatsapp-business-api-template': { text: 'Whatsapp Şablon Mesajı', icon: 'article', isSpecial: true },
            'meta-post': { text: 'Meta Post', icon: 'post_add', isSpecial: true },
            'trendyol-product': { text: 'Trendyol Ürün', icon: 'shopping_cart', isSpecial: true },
            'voice-agent-call': { text: 'Yapay Zeka Araması', icon: 'phone', isSpecial: true },
            'visitor-json': { text: 'JSON Mesaj İçeriği', icon: 'code', isSpecial: true },
            'chatbot-button': { text: 'Chatbot Button', icon: 'smart_button', isSpecial: true },
            'chatbot-quick-reply': { text: 'Chatbot Quick Reply', icon: 'flash_on', isSpecial: true },
            'chatbot-get-location': { text: 'Chatbot Konum Talebi', icon: 'my_location', isSpecial: true },
            'select-reservation-day': { text: 'Rezervasyon Seçimi', icon: 'event', isSpecial: true },
        }
    }),
    computed: {
        lastMessageIsAsset() {
            return !!this.lastMessage?.asset;
        },
        lastMessageIsLocation() {
            return !!this.lastMessage?.location;
        },
        lastMessageIcon() {
            if (!this.lastMessage) return null;

            // Asset kontrolü - özel asset tipleri için
            if (this.lastMessageIsAsset) {
                const asset = this.lastMessage.asset;
                if (asset?.mime) {
                    if (asset.mime.includes('audio')) return 'audiotrack';
                    if (asset.mime.includes('image')) return 'image';
                    if (asset.mime.includes('video')) return 'videocam';
                    return 'description';
                }
                return 'attach_file';
            }

            // Lokasyon için icon
            if (this.lastMessageIsLocation) {
                return 'location_on';
            }

            // Chatbot text mesajları için özel icon
            if (this.lastMessage.messageType === 'chatbot' || this.lastMessage.messageType === 'chatbot-text') {
                return 'smart_toy';
            }
            if (this.lastMessage.messageType === 'chatbot-private') {
                return 'lock';
            }

            // Mapping'den icon al
            const mapping = this.messageTypeMapping[this.lastMessage.messageType];
            if(mapping) {
                return mapping.icon;
            }
            if(this.lastMessage.messageType === "user") return "support_agent";
            if(this.lastMessage.messageType === "visitor") return "person";
            if(this.lastMessage.messageType === "system") return "settings";
            return null;
        },
        lastMessageText() {
            if (!this.lastMessage) return "";

            // Asset kontrolü - özel asset tipleri için
            if (this.lastMessageIsAsset) {
                const asset = this.lastMessage.asset;
                if (asset?.mime) {
                    if (asset.mime.includes('audio')) return "Ses Dosyası";
                    if (asset.mime.includes('image')) return "Media İçeriği";
                    if (asset.mime.includes('video')) return "Media İçeriği";
                    return "Belge";
                }
                return "Media İçeriği";
            }

            // Lokasyon mesajları
            if (this.lastMessageIsLocation) {
                return "Konum Paylaşımı";
            }

            // Mapping'den text al
            const mapping = this.messageTypeMapping[this.lastMessage.messageType];
            if (mapping) {
                return mapping.text;
            }

            // Normal text mesajları ve sistem mesajları için
            const message = this.lastMessage.description || this.lastMessage.message || "";
            // Tüm formatları çıkar (hem HTML hem platform formatları)
            return message ? stripAllFormats(message) : "Mesaj";
        },
        isSpecialMessage() {
            if (!this.lastMessage) return false;

            // Asset varsa özel
            if (this.lastMessageIsAsset || this.lastMessageIsLocation) {
                return true;
            }

            // Chatbot mesajları da özel (text dahil)
            if (this.lastMessage.messageType && this.lastMessage.messageType.startsWith('chatbot')) {
                return true;
            }

            // Mapping'den kontrol et
            const mapping = this.messageTypeMapping[this.lastMessage.messageType];
            return mapping?.isSpecial || false;
        },
        lastMessageSender() {
            return this.lastMessage?.messageType || this.lastMessage?.type || "";
        },
    },
};
</script>

<style lang="scss" scoped>
.last-message {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6b7280;
    min-width: 0;

    .message-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        .material-icons {
            font-size: 14px;
            color: #9ca3af;
        }
    }

    .message-text {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    &.visitor-message .message-text {
        font-weight: 500;
        color: #374151;
    }

    &.agent-message .message-text {
        font-weight: 400;
        color: #6b7280;
    }

    &.special-message .message-text {
        font-style: italic;
        color: #6b7280;
    }
}
</style>

